import React, { useState, useEffect, useRef } from 'react';
import { customersAPI } from '../services/api';
import { User, Search, ChevronDown, X, Plus } from 'lucide-react';
import CityDropdown from './CityDropdown';

const QuickAddCustomerModal = ({ open, onClose, onCustomerCreated, initialPhone }) => {
  const [form, setForm] = useState({ name: '', phone: '', cityId: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      setForm({ name: '', phone: initialPhone || '', cityId: '' });
      setErrors({});
      setLoading(false);
    }
    prevOpen.current = open;
  }, [open, initialPhone]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.cityId) newErrors.cityId = 'City is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const res = await customersAPI.create({
        fullName: form.name.trim(),
        phoneNumber: form.phone.trim(),
        cityId: form.cityId
      });
      onCustomerCreated(res.customer);
      onClose();
    } catch (err) {
      setErrors({ phone: err.response?.data?.error || 'Failed to create customer' });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
        <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={onClose}>
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-bold mb-4">Add New Customer</h2>
        <div className="space-y-4" autoComplete="off">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              disabled={loading}
              autoComplete="off"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="text"
              value={form.phone}
              onChange={e => handleChange('phone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
              disabled={loading}
              autoComplete="off"
            />
            {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <CityDropdown
              value={form.cityId}
              onChange={cityId => handleChange('cityId', cityId)}
              error={!!errors.cityId}
              disabled={loading}
            />
            {errors.cityId && <p className="text-sm text-red-600 mt-1">{errors.cityId}</p>}
          </div>
          <button
            type="button"
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? 'Adding...' : 'Add Customer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomerSearchDropdown = ({
  value,
  onChange,
  placeholder = 'Select customer...',
  disabled = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const dropdownRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Fetch customer by ID if value is set
  useEffect(() => {
    let ignore = false;
    if (value && !selectedCustomer) {
      setIsLoading(true);
      customersAPI.getById(value)
        .then(res => {
          if (!ignore) setSelectedCustomer(res.customer);
        })
        .finally(() => {
          if (!ignore) setIsLoading(false);
        });
    } else if (!value) {
      setSelectedCustomer(null);
    }
    return () => { ignore = true; };
  }, [value]);

  // Debounced server-side search
  useEffect(() => {
    if (!isOpen || !searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      customersAPI.search(searchTerm)
        .then(res => setSearchResults(res.customers || []))
        .finally(() => setIsLoading(false));
    }, 300);
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [searchTerm, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCustomerSelect = (customer) => {
    onChange(customer.id);
    setSelectedCustomer(customer);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSelectedCustomer(null);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCustomerCreated = (customer) => {
    onChange(customer.id);
    setSelectedCustomer(customer);
    setShowAddModal(false);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <div
          className={`w-full px-4 py-3 border rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer ${
            disabled
              ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed'
              : error
              ? 'bg-red-50 border-red-200 text-red-900 hover:bg-red-100'
              : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {selectedCustomer ? (
                <>
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-3 w-3 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{selectedCustomer.full_name}</p>
                    <p className="text-xs text-gray-600">{selectedCustomer.phone_number}</p>
                  </div>
                </>
              ) : (
                <span className="text-gray-500">{placeholder}</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {selectedCustomer && !disabled && (
                <button
                  onClick={handleClear}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </div>

        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers by name or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* Customers List */}
            <div className="max-h-48 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Loading customers...
                </div>
              ) : searchTerm.length < 2 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Type at least 2 characters to search
                </div>
              ) : searchResults.length === 0 ? (
                <>
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No customers found
                  </div>
                  <div className="p-2 text-center">
                    <button
                      className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      onMouseDown={e => { e.preventDefault(); setShowAddModal(true); }}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Customer
                    </button>
                  </div>
                </>
              ) : (
                searchResults.map(customer => (
                  <div
                    key={customer.id}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-3 w-3 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{customer.full_name}</p>
                        <p className="text-sm text-gray-600">{customer.phone_number}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      <QuickAddCustomerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCustomerCreated={handleCustomerCreated}
        initialPhone={
          searchTerm && /^[-+()\d\s]+$/.test(searchTerm.trim()) ? searchTerm.trim() : ''
        }
      />
    </>
  );
};

export default CustomerSearchDropdown; 